import { evaluateCondition } from "./conditions";

const DEFAULT_RESET_VALUE = false;

/**
 * Applies every descriptor's resetIf rule until the flag set stops changing, so a batch of
 * flags loaded from the backend ends up consistent even when reset rules chain
 * (e.g. A resets when B is false, B resets when C is false).
 *
 * Each flag can only be pushed to its resetValue once — a flag already at its resetValue is
 * never touched again — so a chain of N descriptors is guaranteed to settle within N+1 passes.
 * That bound still guards the call: if a future change (e.g. a computed resetValue) breaks that
 * guarantee and the set doesn't stabilize in time, normalization stops, the mismatch is logged,
 * and the partially-normalized flags are returned rather than looping forever.
 *
 * @param {Object} flags - Current flag values, keyed by flag name. Not mutated.
 * @param {import("./conditions").FlagDescriptor[]} descriptors - Descriptors carrying resetIf rules.
 * @param {import("./conditions").FlagConditionContext} [context] - Extra context (app/user) resetIf
 *   conditions may reference. Its `flags` entry is overridden with the working copy each iteration.
 * @param {Object} [options]
 * @param {(message: string, meta: Object) => void} [options.logger] - Called once if the reset
 *   chain doesn't converge. Defaults to console.error.
 * @returns {Object} A new, normalized flags object.
 * @example
 * normalizeFlags(
 *   { bloom_filter_enabled: false, bloom_filter_dual_write_and_read: true },
 *   [{
 *     key: "bloom_filter_dual_write_and_read",
 *     resetIf: { source: "flags", key: "bloom_filter_enabled", eq: false },
 *   }],
 * ); // => { bloom_filter_enabled: false, bloom_filter_dual_write_and_read: false }
 */
export const normalizeFlags = (
  flags,
  descriptors,
  context = {},
  { logger = console.error } = {},
) => {
  const result = { ...flags };
  // +1: each flag can only be forced to its resetValue once (normalizeFlags never moves a flag
  // away from a resetValue it already holds), so a chain of N descriptors fully propagates in at
  // most N passes — one extra pass is needed purely to confirm nothing changed anymore.
  const maxIterations = descriptors.length + 1;
  let changed = true;
  let iterations = 0;

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;

    for (const descriptor of descriptors) {
      if (!descriptor.resetIf) continue;

      const resetValue = "resetValue" in descriptor ? descriptor.resetValue : DEFAULT_RESET_VALUE;
      const shouldReset =
        result[descriptor.key] !== resetValue &&
        evaluateCondition(descriptor.resetIf, { ...context, flags: result });

      if (shouldReset) {
        result[descriptor.key] = resetValue;
        changed = true;
      }
    }
  }

  if (changed) {
    logger("normalizeFlags: reset chain did not converge, possible cycle in resetIf", {
      iterations,
      flags: result,
    });
  }

  return result;
};
